import React from "react";

type TableColumn = { key: string; label?: string; header?: string; align?: "left" | "center" | "right" };
type TableRow = Record<string, any>;

function getCellValue(v: any): string {
  if (v == null) return "";
  if (typeof v === "object" && "value" in v) {
    const maybe = (v as any).display ?? (v as any).value;
    return String(maybe ?? "");
  }
  return String(v);
}

function escapeCsv(s: string): string {
  const needsQuotes = s.includes('"') || s.includes(',') || s.includes('\n');
  const out = s.replace(/"/g, '""');
  return needsQuotes ? '"' + out + '"' : out;
}

function toCSV(columns: TableColumn[], rows: TableRow[]): string {
  const head = columns.map(c => escapeCsv(String(c.label ?? c.header ?? c.key))).join(",");
  const body = rows
    .map(r => columns.map(c => escapeCsv(getCellValue(r[c.key]))).join(","))
    .join("\n");
  return head + "\n" + body;
}

function loadJSZip(): Promise<any> {
  return new Promise((resolve, reject) => {
    const w = window as any;
    if (w.JSZip) return resolve(w.JSZip);
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js';
    script.async = true;
    script.onload = () => resolve((window as any).JSZip);
    script.onerror = () => reject(new Error('Failed to load JSZip'));
    document.head.appendChild(script);
  });
}

async function toXLSXBlob(columns: TableColumn[], rows: TableRow[], filename: string): Promise<Blob> {
  const esc = (s: string) => s.replace(/[<&>"]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' } as any)[c] ?? c);
  const rowsXml = [
    `<row>` + columns.map((c, i) => `<c r="${String.fromCharCode(65 + i)}1" t="inlineStr"><is><t>${esc(String(c.label ?? c.header ?? c.key))}</t></is></c>`).join("") + `</row>`,
    ...rows.map((r, ri) => `<row>` + columns.map((c, ci) => {
      const v = getCellValue(r[c.key]);
      const ref = `${String.fromCharCode(65 + ci)}${ri + 2}`;
      const n = Number(v);
      const isNum = !Number.isNaN(n) && v !== "";
      return isNum ? `<c r="${ref}"><v>${n}</v></c>` : `<c r="${ref}" t="inlineStr"><is><t>${esc(String(v))}</t></is></c>`;
    }).join("") + `</row>`)
  ].join("");
  const sheetXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${rowsXml}</sheetData></worksheet>`;
  const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheets><sheet name="Sheet1" sheetId="1" r:id="rId1"/></sheets></workbook>`;
  const relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>`;
  const contentTypesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/></Types>`;

  const JSZip = await loadJSZip();
  const zip = new JSZip();
  zip.file('[Content_Types].xml', contentTypesXml);
  zip.folder('_rels')?.file('.rels', relsXml);
  const xl = zip.folder('xl');
  xl?.file('workbook.xml', workbookXml);
  xl?.folder('_rels')?.file('workbook.xml.rels', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/></Relationships>`);
  xl?.folder('worksheets')?.file('sheet1.xml', sheetXml);
  const blob = await zip.generateAsync({ type: 'blob' });
  return blob as Blob;
}

export default function TableBlock({ payload }: { payload: any }) {
  const columns: TableColumn[] = Array.isArray(payload?.columns) ? payload.columns : [];
  const rows: TableRow[] = Array.isArray(payload?.rows) ? payload.rows : [];
  const filename = String(payload?.download?.filename || payload?.title || 'table').replace(/\s+/g, '_').toLowerCase();

  const handleCsv = () => {
    const csv = toCSV(columns, rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${filename}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 0);
  };

  const handleXlsx = async () => {
    try {
      const blob = await toXLSXBlob(columns, rows, filename);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${filename}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 0);
    } catch {
      // Fallback to CSV on error
      handleCsv();
    }
  };

  return (
    <div className="w-full overflow-x-auto border border-border rounded-lg bg-window">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <div className="text-sm font-semibold">{String(payload?.title || 'Table')}</div>
        <div className="flex items-center gap-2">
          <button onClick={handleCsv} className="text-xs border border-border px-2 py-1 rounded hover:bg-window/80">CSV</button>
          <button onClick={handleXlsx} className="text-xs border border-border px-2 py-1 rounded hover:bg-window/80">XLSX</button>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={`text-left px-3 py-2 border-b border-border ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'}`}>
                {c.label ?? c.header ?? c.key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={idx} className={idx % 2 === 1 ? 'bg-secondary/20' : ''}>
              {columns.map((c) => (
                <td key={c.key} className={`px-3 py-2 border-b border-border ${c.align === 'right' ? 'text-right' : c.align === 'center' ? 'text-center' : 'text-left'}`}>
                  {getCellValue(r[c.key])}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={Math.max(columns.length, 1)} className="px-3 py-4 text-muted-foreground text-sm">No data</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


